import React from "react";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Delete as DeleteIcon,
  FileCopy as FileCopyIcon,
} from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.background.paper,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
  },
  title: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
  },
  content: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "220px 1fr",
    gap: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  newButton: {
    marginTop: theme.spacing(1),
  },
  templateListItem: {
    // MUI's own `selected` prop is just a faint background tint - easy
    // to miss, especially in dark mode. A colored left border reads as
    // "this one" at a glance, same idea as the team-color left borders
    // already used on Upcoming's event cards.
    borderLeft: "3px solid transparent",
  },
  templateListItemSelected: {
    borderLeftColor: theme.palette.secondary.main,
  },
  sectionRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  editorActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  deleteButton: {
    color: "#f44336",
    borderColor: "#f44336",
    marginLeft: "auto",
  },
}));

const emptyDraft = { id: null, name: "", sections: [] };

const TemplateEditor = ({ template, seedDraft, onSave, onDelete, onSetDefault, onFeedback }) => {
  const classes = useStyles();
  // seedDraft (only ever set alongside a null `template`, i.e. while
  // creating) pre-fills a new draft from another template's sections -
  // see handleDuplicate below. Falls back to a genuinely blank draft the
  // rest of the time.
  const [draft, setDraft] = React.useState(template || seedDraft || emptyDraft);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const handleNameChange = (event) => setDraft({ ...draft, name: event.target.value });

  const handleSectionNameChange = (index, value) => {
    const sections = [...draft.sections];
    sections[index] = { ...sections[index], name: value };
    setDraft({ ...draft, sections });
  };

  const handleAddSection = () => {
    setDraft({
      ...draft,
      sections: [...draft.sections, { name: "", order: draft.sections.length }],
    });
  };

  const handleRemoveSection = (index) => {
    const sections = draft.sections
      .filter((_, i) => i !== index)
      .map((section, i) => ({ ...section, order: i }));
    setDraft({ ...draft, sections });
  };

  const handleMoveSection = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= draft.sections.length) return;
    const sections = [...draft.sections];
    [sections[index], sections[target]] = [sections[target], sections[index]];
    setDraft({ ...draft, sections: sections.map((section, i) => ({ ...section, order: i })) });
  };

  const handleSave = () => {
    setSaving(true);
    onSave(draft)
      .then(() => onFeedback("success", "Template saved"))
      .catch(() => onFeedback("error", "Couldn't save the template - try again"))
      .finally(() => setSaving(false));
  };

  const hasEmptySectionName = draft.sections.some((section) => !section.name.trim());

  return (
    <Paper>
      <Box p={3}>
        <TextField
          label="Template name"
          value={draft.name}
          onChange={handleNameChange}
          fullWidth
          color="secondary"
        />

        {draft.id && (
          <Box mt={2}>
            {template && template.is_default ? (
              <Chip label="Default template" color="secondary" size="small" />
            ) : (
              <Button
                size="small"
                onClick={() =>
                  onSetDefault(draft.id)
                    .then(() => onFeedback("success", "Default template updated"))
                    .catch(() => onFeedback("error", "Couldn't update the default template - try again"))
                }
              >
                Set as team default
              </Button>
            )}
          </Box>
        )}

        <Box mt={3}>
          <Typography variant="subtitle2" color="textSecondary">
            Sections, in order
          </Typography>
          <List>
            {draft.sections.map((section, index) => (
              <ListItem key={index} className={classes.sectionRow} disableGutters>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => handleMoveSection(index, -1)}
                  title="Move up"
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === draft.sections.length - 1}
                  onClick={() => handleMoveSection(index, 1)}
                  title="Move down"
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <TextField
                  value={section.name}
                  onChange={(event) => handleSectionNameChange(index, event.target.value)}
                  placeholder="Section name"
                  fullWidth
                />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSection(index)}
                    title="Remove section"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddSection}>
            Add section
          </Button>
        </Box>

        <Box className={classes.editorActions}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSave}
            disabled={saving || !draft.name.trim() || hasEmptySectionName}
          >
            Save
          </Button>
          {draft.id && (
            <Button
              variant="outlined"
              className={classes.deleteButton}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Delete template
            </Button>
          )}
        </Box>
      </Box>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete this template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Events currently using "{draft.name}" will fall back to a flat setlist -
            nothing on them is deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} autoFocus>
            Cancel
          </Button>
          <Button
            style={{ color: "#f44336" }}
            onClick={() => {
              setConfirmDeleteOpen(false);
              onDelete(draft.id)
                .then(() => onFeedback("success", "Template deleted"))
                .catch(() => onFeedback("error", "Couldn't delete the template - try again"));
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ({
  templates,
  selectedId,
  onSelect,
  onCreateNew,
  onSave,
  onDelete,
  onSetDefault,
  onBack,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  // Same breakpoint Navigationbar.jsx switches on. Desktop already has a
  // persistent "Setlist Templates" tab in its own top nav - this page's
  // own back-arrow app bar is redundant there (and Navigationbar.jsx no
  // longer hides its nav for this route on desktop, only mobile), so it
  // renders bare content instead, exactly like Calendar/Upcoming/Catalog
  // already do. Mobile has no bottom-tab entry for this page, so it
  // still needs its own back button.
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const selectedTemplate =
    selectedId === "new" ? null : templates.find((t) => t.id === selectedId) || null;
  // Lives here, not in TemplateEditor - a delete (or switching selection)
  // unmounts that editor (it's keyed on selectedId), which would take a
  // locally-owned snackbar down with it before the user ever saw it.
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (severity, message) => setSnackbar({ open: true, message, severity });
  // Which template (if any) a fresh "new" draft should be pre-filled
  // from - see handleDuplicate. Cleared on any other selection change so
  // it never leaks into an unrelated "New template" click.
  const [duplicateFrom, setDuplicateFrom] = React.useState(null);

  const handleSelect = (id) => {
    setDuplicateFrom(null);
    onSelect(id);
  };

  const handleCreateNew = () => {
    setDuplicateFrom(null);
    onCreateNew();
  };

  const handleDuplicate = (event, template) => {
    event.stopPropagation();
    setDuplicateFrom(template);
    onSelect("new");
  };

  const seedDraft = duplicateFrom && {
    id: null,
    name: `${duplicateFrom.name} copy`,
    // Fresh, id-less sections - these need to become new rows of their
    // own, not somehow reference the source template's existing ones.
    sections: duplicateFrom.sections.map((section) => ({ name: section.name, order: section.order })),
  };

  return (
    <>
      {!isDesktop && (
        <AppBar position="sticky" color="default" className={classes.appBar} elevation={1}>
          <Toolbar className={classes.toolbar}>
            <IconButton edge="start" aria-label="back" title="Back" onClick={onBack}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" className={classes.title}>
              Setlist templates
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Container maxWidth="md" className={classes.content}>
        <div className={classes.layout}>
          <div>
            <List>
              {templates.map((template) => (
                <ListItem
                  key={template.id}
                  button
                  selected={template.id === selectedId}
                  onClick={() => handleSelect(template.id)}
                  className={
                    template.id === selectedId
                      ? `${classes.templateListItem} ${classes.templateListItemSelected}`
                      : classes.templateListItem
                  }
                >
                  <ListItemText
                    primary={template.name}
                    secondary={template.is_default ? "Default" : null}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      title="Duplicate - start a new template from this one's sections"
                      onClick={(event) => handleDuplicate(event, template)}
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            <Button className={classes.newButton} startIcon={<AddIcon />} onClick={handleCreateNew}>
              New template
            </Button>
          </div>

          <div>
            {selectedId !== null && (
              <TemplateEditor
                key={selectedId}
                template={selectedTemplate}
                seedDraft={seedDraft}
                onSave={onSave}
                onDelete={onDelete}
                onSetDefault={onSetDefault}
                onFeedback={showSnackbar}
              />
            )}
          </div>
        </div>
      </Container>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={snackbar.severity === "error" ? 4000 : 2000}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
